import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { definePersistenceEntry, resourceScopeCleared, userScopeCleared, type ModulePersistenceEntry } from '@krp/client-runtime'

import { normalizeRid } from './keys'
import type { ChosenWarehouse } from './types'

/**
 * Склад, выбранный для инвентаризации в КАЖДОЙ базе (срок `device`, по
 * пользователю): после F5 окно само открывает обход того же склада, и
 * сессия продолжается с сервера. Склад — внутри базы: одинаковый GUID склада
 * в двух базах — два независимых выбора.
 *
 * Хранится ключ и подпись выбора, а не склады базы (RP-LSB2): подпись видна
 * только до ответа живой структуры 1С; ответ её сверяет — имя расходится —
 * берётся имя из 1С, склада в 1С больше нет — выбор снимается
 * (`warehouseChoiceDropped`), и окно говорит об этом словами.
 */
export type WarehouseChoiceState = Record<string, ChosenWarehouse>

const initialState: WarehouseChoiceState = {}

const warehouseChoiceSlice = createSlice({
  name: 'onec/inventoryWarehouse',
  initialState,
  reducers: {
    warehouseChosen(state, action: PayloadAction<{ resourceId: string; warehouse: ChosenWarehouse }>) {
      state[normalizeRid(action.payload.resourceId)] = {
        refKey: action.payload.warehouse.refKey.trim().toLowerCase(),
        name: action.payload.warehouse.name,
      }
    },
    warehouseChoiceRestored(_state, action: PayloadAction<WarehouseChoiceState>) {
      return action.payload
    },
    /** Склада больше нет в ответе 1С: выбор этой базы снимается (только если выбран всё ещё он). */
    warehouseChoiceDropped(state, action: PayloadAction<{ resourceId: string; warehouse: ChosenWarehouse }>) {
      const rid = normalizeRid(action.payload.resourceId)
      if (state[rid]?.refKey === action.payload.warehouse.refKey.trim().toLowerCase()) delete state[rid]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      delete state[normalizeRid(action.payload.resourceId)]
    })
  },
})

export const { warehouseChosen, warehouseChoiceRestored, warehouseChoiceDropped } = warehouseChoiceSlice.actions
export const warehouseChoiceReducer = warehouseChoiceSlice.reducer

/** Снимок вкладки и запись хранения: склад одной базы. */
export function decodeChosenWarehouse(raw: unknown): ChosenWarehouse | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const { refKey, name } = raw as { refKey?: unknown; name?: unknown }
  return typeof refKey === 'string' && refKey.trim() !== '' && typeof name === 'string'
    ? { refKey: refKey.trim().toLowerCase(), name }
    : undefined
}

export function decodeWarehouseChoice(raw: unknown): WarehouseChoiceState | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined
  const result: WarehouseChoiceState = {}
  for (const [rid, value] of Object.entries(raw)) {
    const chosen = decodeChosenWarehouse(value)
    if (chosen === undefined) return undefined
    result[rid.trim().toLowerCase()] = chosen
  }
  return result
}

export function warehouseChoicePersistence<S>(select: (state: S) => WarehouseChoiceState): ModulePersistenceEntry<S> {
  return definePersistenceEntry<S, WarehouseChoiceState>({
    key: 'onec.inventory-warehouse',
    slice: 'inventoryWarehouse',
    lifetime: 'device',
    scope: 'user',
    version: 1,
    pick: select,
    decode: decodeWarehouseChoice,
    restore: (value) => warehouseChoiceRestored(value),
  })
}
